<template>
	<component
		:is="tag"
		:type="type"
		class="basic-button"
		:class="classes"
		:disabled="isDisabled"
		@click="$emit('click', $event)"
	>
		<div class="basic-button__content">
			<slot />
		</div>
	</component>
</template>

<script setup lang="ts">
import { computed } from 'vue';

type ButtonColors =
	| 'none'
	| 'primary'
	| 'secondary'
	| 'tertiary'
	| 'success'
	| 'danger'
	| 'light'
	| 'medium'
	| 'dark'
	| 'ghost'
	| 'gray'
	| 'grey';

interface BasicButtonProps {
	color?: ButtonColors;
	bordered?: boolean;
	filled?: boolean; // whether the background is filled or not
	blocked?: boolean;
	uppercase?: boolean;
	disabled?: boolean;
	rounded?: boolean;
	size?: 'small' | 'default' | 'medium' | 'large' | 'none';
	tag?: 'button' | 'div';
}

const props = withDefaults(defineProps<BasicButtonProps>(), {
	color: 'secondary',
	filled: true,
	rounded: true,
	size: 'default',
	tag: 'button',
});

defineEmits<{
	(e: 'click', event: MouseEvent): void;
}>();

const type = computed(() => (props.tag === 'button' ? 'button' : undefined));
const isDisabled = computed(() => (props.tag === 'button' ? props.disabled : undefined));

const classes = computed(() => {
	return {
		// colors
		primary: props.color === 'primary',
		secondary: props.color === 'secondary',
		tertiary: props.color === 'tertiary' || props.color === 'dark',
		light: props.color === 'light',
		medium: props.color === 'medium',
		ghost: props.color === 'ghost',
		grey: props.color === 'gray' || props.color === 'grey',

		// size
		sm: props.size === 'small',
		md: props.size === 'default',
		md24: props.size === 'medium',
		lg: props.size === 'large',

		border: props.bordered,
		'w-full': props.blocked,
		'bg-none': !props.filled,
		uppercase: props.uppercase,
		rounded: props.rounded,
		disabled: props.disabled,
		'pointer-events-none': props.disabled && props.tag !== 'button',
	};
});
</script>

<style lang="scss">
@import '~@/styles/variables.scss';

.basic-button {
	// reset
	background: none;
	border: none;
	margin: 0;
	padding: 0;
	outline: none;

	// general
	white-space: nowrap;
	cursor: pointer;

	&__content {
		overflow: hidden;
		text-overflow: ellipsis;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&.sm {
		font-size: 13px;
		padding: 4px 13.5px;
	}

	&.md {
		padding: 7.5px 15px;
	}

	&.md24 {
		padding: 8px 24px;

		&.rounded {
			border-radius: 8px;
		}
	}

	&.lg {
		font-size: 18px;
		padding: 16px 40px;

		&.rounded {
			border-radius: 8px;
		}
	}

	&.w-full {
		width: 100%;
	}

	&.rounded {
		border-radius: $jw-border-radius;
	}

	&.uppercase {
		text-transform: uppercase;
	}

	&.primary {
		font-weight: 700;
		background-color: $jw-color-gold;
		color: $jw-color-black;

		&.bg-none {
			color: $jw-color-gold;
			&:hover {
				background-color: rgba(251, 197, 0, 0.1);
			}
		}

		&.border {
			color: $jw-color-gold--light;
			border: 1px solid $jw-color-gold--light;

			&:hover {
				background-color: rgba(251, 197, 0, 0.1);
			}
		}

		&:hover {
			background-color: $jw-color-gold--dark;
		}
	}

	&.secondary {
		color: white;
		font-weight: 700;
		background-color: $jw-color-mediumblue;

		&.bg-none {
			color: $jw-color-mediumblue;

			&:hover {
				background-color: rgba(94, 107, 118, 0.1);
			}
		}

		&.border {
			border: 1px solid $jw-color-mediumblue--light;

			&:hover {
				background-color: rgba(94, 107, 118, 0.1);
			}
		}

		&:hover {
			background-color: $jw-color-mediumblue--light;
		}
	}

	&.tertiary {
		color: white;
		background-color: $jw-color-darkgray;

		&.bg-none {
			color: $jw-color-darkgray;

			&:hover {
				background-color: rgba(94, 107, 118, 0.1);
			}
		}

		&.border {
			border: 1px solid $jw-color-darkgray--light;

			&:hover {
				background-color: rgba(56, 61, 71, 0.1);
			}
		}

		&:hover {
			background-color: $jw-color-darkgray--light;
		}
	}

	&.light {
		color: $jw-color-lightblue--light;
		background-color: $jw-color-darkgray;

		&:hover {
			background-color: $jw-color-darkgray--light;
		}
	}

	&.medium {
		color: white;
		background-color: $jw-color-mediumgray;

		&.border {
			color: $jw-color-mediumgray--light;
			border: 1px solid $jw-color-mediumgray--light;

			&:hover {
				background-color: rgba(153, 156, 159, 0.1);
			}
		}

		&:hover {
			background-color: $jw-color-mediumgray--light;
		}
	}

	&.ghost {
		color: #d2d3d5;
		background-color: rgba(255, 255, 255, 0.08);

		&:hover {
			background-color: rgba(94, 107, 118, 0.16);
		}
	}

	&.grey {
		color: #fff;
		background-color: #353a42;

		&:hover {
			background-color: $jw-color-darkgray--lighter;
		}
	}

	&.bg-none {
		background: transparent;
	}

	&.disabled {
		cursor: default;
		opacity: 0.5;
	}

	&.pointer-events-none {
		pointer-events: none;
	}
}
</style>
